import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import api from '../../services/api';
import { toast } from 'react-toastify';

const PAYMENT_STATUSES = ['pending', 'paid', 'failed', 'refunded'];

const ManageOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [stats, setStats] = useState({
        total_orders: 0,
        total_revenue: 0,
        pending_orders: 0,
        completed_orders: 0
    });

    // QR modal
    const [qrOrder, setQrOrder] = useState(null);

    // Payment status modal
    const [statusOrder, setStatusOrder] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // Pagination
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchOrders(1);
    }, [debouncedSearch, statusFilter]);

    const fetchOrders = async (pageNumber = 1) => {
        try {
            setLoading(true);
            const params = {
                page: pageNumber,
                search: debouncedSearch || undefined,
                payment_status: statusFilter !== 'all' ? statusFilter : undefined,
            };
            const response = await api.get('/admin/orders', { params });
            const paginator = response.data.data.orders;
            const statsData = response.data.data.stats;
            setOrders(paginator.data || []);
            setPagination(paginator);
            setPage(pageNumber);
            setStats(statsData);
        } catch (error) {
            toast.error('Failed to load orders');
            console.error('Error fetching orders:', error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePaymentStatus = async () => {
        if (!statusOrder || !newStatus) return;
        try {
            setUpdatingStatus(true);
            await api.put(`/admin/orders/${statusOrder.id}/payment-status`, { payment_status: newStatus });
            toast.success('Payment status updated');
            setOrders(prev => prev.map(o => o.id === statusOrder.id ? { ...o, payment_status: newStatus } : o));
            setStatusOrder(null);
        } catch (error) {
            toast.error('Failed to update payment status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;
        const current = pagination.current_page;
        const last = pagination.last_page;
        const maxPages = 3;
        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;
        if (end > last) { end = last; start = Math.max(1, end - maxPages + 1); }
        const pageNumbers = [];
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchOrders(current - 1)}>Previous</button>
                    </li>
                    {start > 1 && (
                        <>
                            <li className="page-item"><button className="page-link" onClick={() => fetchOrders(1)}>1</button></li>
                            {start > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        </>
                    )}
                    {pageNumbers.map(num => (
                        <li key={num} className={`page-item ${num === current ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => fetchOrders(num)}>{num}</button>
                        </li>
                    ))}
                    {end < last && (
                        <>
                            {end < last - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                            <li className="page-item"><button className="page-link" onClick={() => fetchOrders(last)}>{last}</button></li>
                        </>
                    )}
                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchOrders(current + 1)}>Next</button>
                    </li>
                </ul>
            </nav>
        );
    };

    const formatCurrency = (amount) => `$${parseFloat(amount).toFixed(2)}`;
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });

    const getItemLabel = (item) => {
        if (item.ticket_category?.category_name) return item.ticket_category.category_name;
        if (item.section?.name) return `${item.section.name} (GA)`;
        return 'Item';
    };

    const getPaymentStatusBadge = (status) => {
        const badges = {
            pending: 'badge-warning',
            paid: 'badge-success',
            failed: 'badge-danger',
            refunded: 'badge-info'
        };
        return badges[status] || 'badge-secondary';
    };

    const orderTickets = qrOrder?.tickets || [];

    return (
        <div className="container-fluid">
            <div className="d-sm-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0 text-gray-900 font-weight-bold">Manage Orders</h1>
            </div>

            {/* Stats Cards */}
            <div className="row mb-4">
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-primary shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">Total Orders</div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.total_orders}</div>
                                </div>
                                <div className="col-auto"><i className="fas fa-shopping-cart fa-2x text-gray-300"></i></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-success shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">Paid Orders</div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.completed_orders}</div>
                                </div>
                                <div className="col-auto"><i className="fas fa-check-circle fa-2x text-gray-300"></i></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-warning shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">Pending Orders</div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.pending_orders}</div>
                                </div>
                                <div className="col-auto"><i className="fas fa-clock fa-2x text-gray-300"></i></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-info shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">Total Revenue</div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">{formatCurrency(stats.total_revenue)}</div>
                                </div>
                                <div className="col-auto"><i className="fas fa-dollar-sign fa-2x text-gray-300"></i></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="card shadow mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by order number, customer name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-control"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="paid">Paid</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Orders Table */}
            <div className="card shadow mb-4">
                <div className="card-header py-3">
                    <h6 className="m-0 font-weight-bold text-gray-900">All Orders ({orders.length})</h6>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-bordered" width="100%" cellSpacing="0">
                                <thead>
                                    <tr style={{ fontSize: '0.8rem' }}>
                                        <th>Order #</th>
                                        <th>Date</th>
                                        <th>Customer</th>
                                        <th>Event</th>
                                        <th>Organizer</th>
                                        <th>Items</th>
                                        <th>Total</th>
                                        <th>Payment Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.length > 0 ? (
                                        orders.map(order => (
                                            <tr key={order.id} style={{ fontSize: '0.85rem' }}>
                                                <td className="font-weight-bold text-primary">#{order.id}</td>
                                                <td>{formatDate(order.created_at)}</td>
                                                <td>
                                                    {order.user ? (
                                                        <div>
                                                            <div className="font-weight-bold">{order.user.first_name} {order.user.last_name}</div>
                                                            <small className="text-muted">{order.user.email}</small><br />
                                                            <small className="text-muted">{order.user.mobile}</small>
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                                <td>
                                                    <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                                        {order.event?.title || 'N/A'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <small className="text-muted">{order.event?.organizer?.name || 'N/A'}</small>
                                                </td>
                                                <td>
                                                    <div>
                                                        {order.items?.length > 0 ? (
                                                            order.items.map((item, idx) => (
                                                                <div key={idx} className="small">
                                                                    {item.quantity}x {getItemLabel(item)}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            (order.tickets || []).filter(t => t.seat_map_seat_id).map((ticket, idx) => (
                                                                <div key={idx} className="small">
                                                                    1x {[ticket.section?.name, ticket.seat?.label].filter(Boolean).join(' · ') || 'Reserved Seat'}
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="font-weight-bold">
                                                    {parseFloat(order.total_amount - order.discount_total).toFixed(2)} {order.event?.currency}
                                                </td>
                                                <td>
                                                    <span className={`badge ${getPaymentStatusBadge(order.payment_status)}`}>
                                                        {order.payment_status}
                                                    </span>
                                                    <div className="small text-muted">{order.payment_method}</div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => setQrOrder(order)}
                                                            title="View QR Codes"
                                                        >
                                                            <i className="fas fa-qrcode mr-1"></i> QR Codes
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => { setStatusOrder(order); setNewStatus(order.payment_status); }}
                                                            title="Change Payment Status"
                                                        >
                                                            <i className="fas fa-edit mr-1"></i> Status
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="text-center py-4">
                                                {searchTerm ? 'No orders found matching your search' : 'No orders yet'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {renderPagination()}
                        </div>
                    )}
                </div>
            </div>

            {/* QR Codes Modal */}
            {qrOrder && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setQrOrder(null)}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Ticket QR Codes — Order #{qrOrder.id}
                                </h5>
                                <button className="close" onClick={() => setQrOrder(null)}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                {orderTickets.length === 0 ? (
                                    <p className="text-center text-muted">No tickets found for this order.</p>
                                ) : (
                                    <div className="row">
                                        {orderTickets.map((ticket, idx) => (
                                            <div key={ticket.id || idx} className="col-md-6 mb-4 text-center">
                                                <div className="card p-3">
                                                    <div className="d-flex justify-content-center mb-2">
                                                        <QRCode value={ticket.ticket_qr_code} size={160} />
                                                    </div>
                                                    <div className="small font-weight-bold">{ticket.ticket_qr_code}</div>
                                                    {ticket.section?.name && (
                                                        <div className="small text-muted">
                                                            {ticket.section.name}{ticket.seat?.label ? ` · ${ticket.seat.label}` : ''}
                                                        </div>
                                                    )}
                                                    {ticket.scanned_at && (
                                                        <div className="small text-success mt-1">
                                                            <i className="fas fa-check-circle mr-1"></i>
                                                            Scanned {formatDate(ticket.scanned_at)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setQrOrder(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Payment Status Modal */}
            {statusOrder && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setStatusOrder(null)}>
                    <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Change Payment Status — Order #{statusOrder.id}</h5>
                                <button className="close" onClick={() => setStatusOrder(null)}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="font-weight-bold">Payment Status</label>
                                    <select
                                        className="form-control"
                                        value={newStatus}
                                        onChange={e => setNewStatus(e.target.value)}
                                    >
                                        {PAYMENT_STATUSES.map(s => (
                                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="alert alert-warning small mb-0">
                                    Changing the payment status manually will not trigger refunds or payment processing.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setStatusOrder(null)}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleUpdatePaymentStatus}
                                    disabled={updatingStatus || newStatus === statusOrder.payment_status}
                                >
                                    {updatingStatus ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageOrders;
